import { useEffect, useState } from 'react';
import { useChangeMyPassword, useUpdateMyProfile } from '@/features/auth/auth.mutations';
import { useMyProfile } from '@/features/auth/auth.queries';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FormGenderSelect from '@/components/form-inputs/dropdowns/form-gender-select';
import FormDateInput from '@/components/form-inputs/form-date-input';
import FormPasswordInput from '@/components/form-inputs/form-password-input';
import FormTextInput from '@/components/form-inputs/form-text-input';

import {
  ChangePasswordSchema,
  ChangePasswordSchemaType,
  MyProfileSchema,
  MyProfileSchemaType,
} from '../../../types/auth.types';

export const Route = createFileRoute('/_app/(profile)/profile/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation(['form_field']);

  const { data: profile, isLoading } = useMyProfile();
  const updateProfileMutation = useUpdateMyProfile();
  const changePasswordMutation = useChangeMyPassword();

  // Tabs state
  const [activeTab, setActiveTab] = useState('profile');

  // Form 1: PROFILE
  const profileForm = useForm<MyProfileSchemaType>({
    resolver: zodResolver(MyProfileSchema),
    mode: 'onChange',
    defaultValues: profile,
  });

  // Form 2: SECURITY
  const securityForm = useForm<ChangePasswordSchemaType>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      username: profile?.username ?? '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  // When profile loads → reset profile form
  useEffect(() => {
    if (profile) {
      profileForm.reset(profile);
      securityForm.reset({
        username: profile.username,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [profile, profileForm, securityForm]);

  // Submit profile update
  const onSubmitProfile = async (data: MyProfileSchemaType) => {
    await updateProfileMutation.mutateAsync(data);
  };

  // Submit password change
  const onSubmitPassword = async (data: ChangePasswordSchemaType) => {
    await changePasswordMutation.mutateAsync(data);
    securityForm.reset();
  };

  if (isLoading) return <Loader2 className='m-auto animate-spin' />;

  return (
    <div className='bg-bgPrimary min-h-screen w-full overflow-y-auto py-6'>
      <Tabs value={activeTab} onValueChange={setActiveTab} className='mx-auto w-full max-w-3xl'>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='profile'>Προφίλ</TabsTrigger>
          <TabsTrigger value='security'>Ασφάλεια</TabsTrigger>
        </TabsList>

        {/* ---------------------------------- */}
        {/* PROFILE TAB */}
        {/* ---------------------------------- */}
        <TabsContent value='profile'>
          <Card>
            <CardHeader className='text-center'>
              <h1 className='text-xl font-bold'>Στοιχεία Λογαριασμού</h1>
            </CardHeader>
            <CardContent>
              <Form {...profileForm}>
                <form
                  className='grid grid-cols-1 gap-4 md:grid-cols-2'
                  onSubmit={profileForm.handleSubmit(onSubmitProfile)}
                >
                  <FormTextInput
                    name='firstName'
                    label={t('label.name')}
                    placeholder={t('label.name')}
                  />
                  <FormTextInput
                    name='lastName'
                    label={t('label.last_name')}
                    placeholder={t('label.last_name')}
                  />
                  <FormGenderSelect name='gender' label={t('label.gender')} />

                  <FormDateInput
                    name='birthDate'
                    label={t('label.birthDate')}
                    placeholder={t('label.birthDate')}
                    dateFormat={'dd/MM/yyyy'}
                  />

                  <FormTextInput
                    name='phone'
                    label={t('label.phone')}
                    placeholder={t('label.phone')}
                  />
                  <FormTextInput name='email' label={'Email'} placeholder={'Email'} />

                  <div className='col-span-2 text-right'>
                    <Button type='submit' disabled={updateProfileMutation.isPending}>
                      {updateProfileMutation.isPending ? (
                        <Loader2 className='animate-spin' />
                      ) : (
                        'Ενημέρωση κωδικού'
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------------------------- */}
        {/* SECURITY TAB */}
        {/* ---------------------------------- */}
        <TabsContent value='security'>
          <Card>
            <CardHeader className='text-center'>
              <h1 className='text-xl font-bold'>Στοιχεία Σύνδεσης</h1>
            </CardHeader>

            <CardContent>
              <Form {...securityForm}>
                <form
                  className='grid grid-cols-1 gap-4'
                  onSubmit={securityForm.handleSubmit(onSubmitPassword)}
                >
                  <FormTextInput
                    name={'username'}
                    label={t('label.username')}
                    placeholder={t('label.username')}
                    disabled
                  />
                  <FormPasswordInput
                    name='currentPassword'
                    label={t('label.password')}
                    placeholder={t('label.password')}
                  />
                  <FormPasswordInput
                    name='newPassword'
                    label={t('label.new_password')}
                    placeholder={t('label.new_password')}
                  />
                  <FormPasswordInput
                    name='confirmPassword'
                    label={t('label.confirm_password')}
                    placeholder={t('label.confirm_password')}
                  />

                  <div className='text-right'>
                    <Button type='submit' disabled={changePasswordMutation.isPending}>
                      {changePasswordMutation.isPending ? (
                        <Loader2 className='animate-spin' />
                      ) : (
                        'Αλλαγή κωδικού'
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
